/**
 * Generatore avatar (Andrea 2026-09-29): il suo avatar-generator.html, con il
 * template-avatar.svg accanto, servito da /public/avatar-utente e aperto a
 * schermo intero in un iframe.
 *
 * Protocollo postMessage (solo stessa origine):
 * - avatar:ready   generatore → app: pronto, l'app manda le scelte salvate
 * - avatar:prefill app → generatore: { stato } per riaprire l'avatar com'era
 * - avatar:save    generatore → app: { image: PNG 512 data-URL, stato }
 *
 * Il template si aggiorna da solo (nuove maglie, capelli, accessori): basta
 * sostituire public/avatar-utente/template-avatar.svg.
 */

import { useEffect, useRef, useState } from "react";
import { tr } from "../../i18n";

export interface AvatarSalvato {
  image: string;
  stato: Record<string, unknown>;
}

export function GeneratoreAvatarModal({
  statoIniziale,
  onSalva,
  onClose,
}: {
  statoIniziale: Record<string, unknown> | null | undefined;
  onSalva: (avatar: AvatarSalvato) => Promise<void>;
  onClose: () => void;
}) {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [stato, setStato] = useState<"pronto" | "salvo" | "fatto" | "errore">("pronto");
  const [errore, setErrore] = useState<string | null>(null);

  useEffect(() => {
    const onMessage = async (e: MessageEvent) => {
      if (e.origin !== window.location.origin) return;
      const msg = e.data as { type?: string; payload?: Partial<AvatarSalvato> } | null;
      if (!msg?.type) return;
      if (msg.type === "avatar:ready") {
        if (statoIniziale) {
          iframeRef.current?.contentWindow?.postMessage(
            { type: "avatar:prefill", payload: { stato: statoIniziale } },
            window.location.origin,
          );
        }
        return;
      }
      if (msg.type === "avatar:save" && msg.payload?.image) {
        setStato("salvo");
        setErrore(null);
        try {
          await onSalva({ image: msg.payload.image, stato: msg.payload.stato ?? {} });
          setStato("fatto");
        } catch (err) {
          const e2 = err as { response?: { data?: { detail?: string } }; message?: string };
          setErrore(e2.response?.data?.detail ?? e2.message ?? tr("Errore"));
          setStato("errore");
        }
      }
    };
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex flex-col">
      <div className="flex items-center justify-between gap-2 px-3 py-2 bg-primary-deep border-b border-accent/30">
        <div className="min-w-0">
          <div className="font-display text-accent text-base leading-tight">
            {tr("Il tuo avatar")}
          </div>
          <div className="text-[10px] text-fg-muted">
            {stato === "pronto" && tr("Scegli e premi «Salva Avatar»")}
            {stato === "salvo" && tr("Salvo l'avatar…")}
            {stato === "fatto" && tr("✓ Avatar salvato! Puoi chiudere.")}
            {stato === "errore" && tr("⚠ {{v1}}", { v1: errore })}
          </div>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="shrink-0 px-3 py-1.5 rounded-card border border-accent/40 text-accent text-sm hover:bg-accent/10"
        >
          {stato === "fatto" ? tr("Chiudi") : tr("✕ Chiudi")}
        </button>
      </div>
      <iframe
        ref={iframeRef}
        src="/avatar-utente/avatar-generator.html"
        title={tr("Generatore avatar")}
        className="flex-1 w-full bg-bg"
      />
    </div>
  );
}
